import { LitElement, html } from 'lit';
import styles from './studio-breadcrumbs.css.mjs';
import '../studio-icon/studio-icon.mjs';

export class StudioBreadcrumbs extends LitElement {
  static styles = styles;

  static properties = {
    crumbs: { type: Array },
  };

  static validStates = ['default', 'hover', 'focus', 'selected', 'disabled'];

  constructor() {
    super();
    this.crumbs = [];
    this._eventListeners = [];
  }

  set crumbs(value) {
    this._crumbs = Array.isArray(value)
      ? value.filter(c => c && typeof c === 'object' && 'text' in c).map(c => ({
          text: String(c.text),
          state: StudioBreadcrumbs.validStates.includes(c.state) ? c.state : 'default',
          icon: c.icon ? String(c.icon) : '',
        }))
      : [];
  }

  get crumbs() {
    return this._crumbs;
  }

  firstUpdated() {
    const items = this.renderRoot.querySelectorAll('.item');
    items.forEach(item => {
      const setState = (state, add = true) => {
        if (item.dataset.state !== 'disabled' && item.dataset.state !== 'selected') {
          item.dataset.state = add ? state : 'default';
        }
      };

      const listeners = [
        { event: 'mouseover', handler: () => setState('hover') },
        { event: 'mouseout', handler: () => setState('hover', false) },
        { event: 'focus', handler: () => setState('focus') },
        { event: 'blur', handler: () => setState('focus', false) },
        { event: 'keydown', handler: (e) => {
          if ((e.key === 'Enter' || e.key === ' ') && item.dataset.state !== 'disabled') {
            e.preventDefault();
            item.click();
          }
        }},
      ];

      listeners.forEach(({ event, handler }) => {
        item.addEventListener(event, handler);
        this._eventListeners.push({ element: item, event, handler });
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  _truncateText(text, limit = 12) {
    return text.length > limit ? text.substring(0, limit) + '…' : text;
  }

  _handleClick(index) {
    const crumb = this.crumbs[index];
    if (crumb.state === 'disabled') return;

    this.crumbs = this.crumbs.map((c, i) => ({
      ...c,
      state: c.state === 'disabled' ? 'disabled' : i === index ? 'selected' : 'default',
    }));

    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent('crumb-click', {
        detail: { index, text: crumb.text },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    return html`
      <nav
        class="breadcrumbs"
        role="navigation"
        aria-label="Breadcrumb"
      >
        ${this.crumbs.map((crumb, index) => html`
          <div class="crumb">
            <button
              class="item"
              data-state="${crumb.state}"
              role="link"
              aria-current="${crumb.state === 'selected' ? 'page' : null}"
              ?disabled="${crumb.state === 'disabled'}"
              @click="${() => this._handleClick(index)}"
            >
              <slot name="icon-${index}">
                ${crumb.icon
                  ? html`<studio-icon class="icon" name="${crumb.icon}" size="1rem"></studio-icon>`
                  : ''}
              </slot>
              <slot name="text-${index}">
                <span class="text" title="${crumb.text}">
                  ${this._truncateText(crumb.text)}
                </span>
              </slot>
            </button>
            <slot name="separator-${index}">
              ${index < this.crumbs.length - 1
                ? html`<studio-icon class="separator" name="chevron-right" size="1rem"></studio-icon>`
                : ''}
            </slot>
          </div>
        `)}
        <slot></slot>
      </nav>
    `;
  }
}

customElements.define('studio-breadcrumbs', StudioBreadcrumbs);